import { theme as defaultTheme } from "@mindlercare/ui-web";
import { Tab, Tabs } from "@mui/material";
import { useQuestionnaireTableData } from "api/useQuestionnaireQueries";
import StatusIndicator from "components/lib/statusIndicator";
import CONSTANTS from "core/constants";
import { useTranslation } from "react-i18next";
import { generatePath, useMatch, useNavigate } from "react-router-dom";
import { styled } from "styled-components";
import { useMindlerApi } from "api/apiv2";
import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useQuery } = ReactQueryV5;
import useMarketStrategy from "../../../strategies/market/useMarketStrategy";
import { getQuestionnaireStatus } from "./getQuestionnairesStatus";

const PsychologistPatientCardPath =
  "patients/psychologist/:psychologistId/patient/:patientUserId";

const StyledTabs = styled(Tabs)`
  margin-bottom: ${(p) => defaultTheme.MARGIN.MD};
`;

const StyledTab = styled(Tab)`
  &:hover {
    color: ${(p) => defaultTheme.COLORS.GRAY_BLACK};
    transition: all 200ms ease-in-out;
  }
`;
const StyledRow = styled.div`
  display: flex;
  justify-items: left;
  gap: 2px;
`;
type PatientCardTabsProps = {
  patientUserId?: string | number;
};

export const PatientCardTabs = ({ patientUserId }: PatientCardTabsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const match = useMatch(`${PsychologistPatientCardPath}/:tab`);

  const apiv2 = useMindlerApi();
  const { data: patient } = useQuery(
    apiv2.patients.getByUserId.queryOptions(
      {
        patientUserId: Number(patientUserId),
      },
      {
        enabled: !!patientUserId,
      },
    ),
  );

  const { data: patientQuestionnaireAnswersAndRequests } =
    useQuestionnaireTableData({
      patientUserId: patient?.userId,
    });

  const status = getQuestionnaireStatus(
    patientQuestionnaireAnswersAndRequests,
    t,
  );

  const marketStrategy = useMarketStrategy();
  if (!marketStrategy) return null;

  const disabledTabs: string[] = [];

  const patientCardTabs = Object.values(marketStrategy.patientCardTabs).filter(
    (tab) => !disabledTabs.includes(tab),
  );
  const patientCardTabIndex = patientCardTabs.findIndex(
    (patientTab) => patientTab.toLowerCase() === match?.params?.tab,
  );
  const tabIndex = patientCardTabIndex < 0 ? 0 : patientCardTabIndex;

  const onTabChange = (_: React.SyntheticEvent, idx: number) => {
    navigate(
      `${generatePath(`/${PsychologistPatientCardPath}/:tab`, {
        patientUserId: match?.params?.patientUserId ?? null,
        psychologistId: match?.params?.psychologistId ?? null,
        tab: patientCardTabs[idx].toLowerCase(),
      })}`,
      {
        replace: true,
      },
    );
  };

  return (
    <>
      <StyledTabs value={tabIndex} onChange={onTabChange} scrollButtons="auto">
        {patientCardTabs.map((tab) => (
          <StyledTab
            key={tab}
            label={
              <StyledRow>
                {t(tab)}
                {shouldIndicate(tab, status) && (
                  <StatusIndicator
                    variant={status?.variant ?? "hidden"}
                    noMargin
                  />
                )}
              </StyledRow>
            }
            disableRipple
            data-testid={`cy-patient-${tab.toLowerCase()}-tab`}
          />
        ))}
      </StyledTabs>
    </>
  );
};

function shouldIndicate(
  tab: string,
  status: ReturnType<typeof getQuestionnaireStatus>,
) {
  if (!status || tab !== CONSTANTS.PATIENT_CARD_TABS.ACTIONS) {
    return false;
  }
  if (status.intent === "NEW_RESPONSE") {
    return true;
  }
  return false;
}
